// axiosSetup.js

import axios from "axios";
import { BASE_URL } from "../configs/WebServiceUrl";
import { apiKeyHeader } from "../configs/ApiKeys";

axios.defaults.baseURL = `${BASE_URL}/api`;
axios.defaults.withCredentials = true;
axios.defaults.timeout = 30000;

const REFRESH_TOKEN_URL = `${BASE_URL}/api/adminauth/refresh-token`;

const AUTH_SKIP_PATHS = [
  "/api/adminauth/refresh-token",
  "/api/adminauth/login",
  "/api/adminauth/logout",
  "/api/adminauth/sendotp",
  "/api/adminauth/sendOTPMobileNo",
  "/api/adminauth/verifiedotp",
  "/api/adminauth/verifiedotpmob",
];

const CREDENTIALS_ALLOWED_ORIGINS = [
  BASE_URL
];

const authErrorMessages = [
  "Invalid or expired token",
  "Session expired. Please login again.",
];

const shouldSkipAuth = (url = "") => {
  return AUTH_SKIP_PATHS.some((path) => url.includes(path));
};

const isRefreshTokenRequest = (url = "") => {
  return url.includes("/api/adminauth/refresh-token");
};

const isAuthErrorResponse = (response) => {
  return response?.status === 401 && authErrorMessages.includes(response?.data?.message);
};

const getAccessToken = () => {
  return localStorage.getItem("userToken");
};

const setAccessToken = (token) => {
  localStorage.setItem("userToken", token);
};

const clearAuthStorage = () => {
  localStorage.removeItem("userData");
  localStorage.removeItem("userToken");
  localStorage.removeItem("mdm_org_user");
  localStorage.removeItem("demoAlertShown");
};

const logoutAndRedirect = () => {
  clearAuthStorage();

  if (window.location.pathname !== "/login") {
    window.location.replace("/login");
  }
};

const normalizeUrl = (url = "", baseURL = "") => {
  try {
    if (!url) return "";

    // Absolute URL
    if (/^https?:\/\//i.test(url)) {
      return url;
    }

    // Relative URL + baseURL
    if (baseURL) {
      return new URL(url, baseURL).href;
    }

    return url;
  } catch (error) {
    return url;
  }
};

const shouldUseCredentials = (config = {}) => {
  const fullUrl = normalizeUrl(config.url || "", config.baseURL || axios.defaults.baseURL || "");

  return CREDENTIALS_ALLOWED_ORIGINS.some((origin) => fullUrl.startsWith(origin));
};

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });

  failedQueue = [];
};

const refreshAccessToken = async () => {
  const response = await axios.post(
    REFRESH_TOKEN_URL,
    {},
    {
      withCredentials: true,
      headers: apiKeyHeader(),
    }
  );

  const newToken = response.data?.token || response.data?.accessToken;

  if (!newToken) {
    throw new Error("No new access token received");
  }

  setAccessToken(newToken);
  return newToken;
};

axios.interceptors.request.use(
  (config) => {
    const token = getAccessToken();

    config.headers = {
      ...(config.headers || {}),
      ...apiKeyHeader(),
    };

    // Add bearer token for internal API calls unless skipped
    if (token && !shouldSkipAuth(config.url || "")) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Decide credentials per request
    config.withCredentials = shouldUseCredentials(config);

    return config;
  },
  (error) => Promise.reject(error)
);

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (!originalRequest) {
      return Promise.reject(error);
    }

    if (isRefreshTokenRequest(originalRequest.url || "") && isAuthErrorResponse(error.response)) {
      logoutAndRedirect();
      return Promise.reject(error);
    }

    if (shouldSkipAuth(originalRequest.url || "")) {
      return Promise.reject(error);
    }

    if (error.response?.status !== 401) {
      return Promise.reject(error);
    }

    if (!authErrorMessages.includes(error.response?.data?.message)) {
      return Promise.reject(error);
    }

    if (originalRequest._retry) {
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then((token) => {
        originalRequest.headers = {
          ...(originalRequest.headers || {}),
          Authorization: `Bearer ${token}`,
        };
        originalRequest.withCredentials = shouldUseCredentials(originalRequest);
        return axios(originalRequest);
      });
    }

    isRefreshing = true;

    try {
      const newToken = await refreshAccessToken();

      processQueue(null, newToken);

      originalRequest.headers = {
        ...(originalRequest.headers || {}),
        Authorization: `Bearer ${newToken}`,
      };

      originalRequest.withCredentials = shouldUseCredentials(originalRequest);

      return axios(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError, null);
      logoutAndRedirect();
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);

export default axios;
